import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpHeaders,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import {
  retry,
  catchError,
  finalize,
  tap,
  map,
  switchMap,
} from 'rxjs/operators';
import Store, { AppKey } from '../../store';
import { User } from 'src/app/models/User';
import { ErrorResponse } from 'src/app/models/ErrorResponse';
import { EncryptionService } from 'src/app/core/services/encryption.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class AppInterceptorService implements HttpInterceptor {
  store = new Store();
  // sessionKeyString: string | null = null;

  // sessionMap = new Map<string, string>();
  sessionKeyString: string | null = null;

  constructor(private encryptionService: EncryptionService) {
    if (environment.production) {
      this.encryptionService.enumerator.subscribe((key) => {
        if (key) {
          this.sessionKeyString = key;
        }
      });
    }
  }

  private async prepareRequest(
    request: HttpRequest<any>
  ): Promise<HttpRequest<string>> {
    try {
      const user = this.store.get(AppKey.USER);

      const payload =
        typeof request.body === 'string'
          ? request.body
          : JSON.stringify(request.body);

      if (environment.production) {
        const encryptedKey = await this.encryptionService.encryptKey(
          this.sessionKeyString as string
        );

        const encryptedBody = this.encryptionService.encrypt(
          payload,
          this.sessionKeyString as string
        );

        const clonedRequest = request.clone({
          headers: request.headers
            .set('Content-Type', 'text/plain')
            .set(
              'Authorization',
              user && (user as User).Token ? (user as User).Token : ''
            )
            .set('SKey', encryptedKey || ''),
          responseType: 'text',
          body: encryptedBody,
        });

        return clonedRequest;
      } else {
        const clonedRequest = request.clone({
          headers: request.headers
            .set('Content-Type', 'application/json')
            .set(
              'Authorization',
              user && (user as User).Token ? (user as User).Token : ''
            ),
          body: payload,
        });

        return clonedRequest;
      }
    } catch (error) {
      throw error;
    }
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return from(this.prepareRequest(request)).pipe(
      switchMap((req) => next.handle(req)),
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const body = event.body;
          if (body && typeof body === 'string' && environment.production) {
            try {
              // const sessionKeyString = this.sessionMap.get(request.url);
              const decryptedData = this.encryptionService.decrypt(
                body,
                this.sessionKeyString as string
              );
              // this.sessionMap.delete(request.url);
              const parsedData = JSON.parse(decryptedData);
              return event.clone({ body: parsedData });
            } catch (error) {
              // Return original response if decryption fails instead of throwing
              return event;
            }
          }
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        let errorMsg = 'An error occurred';
        try {
          if (error.error) {
            try {
              // const sessionKeyString = this.sessionMap.get(request.url);
              // this.sessionMap.delete(request.url);

              const parsedData = environment.production
                ? JSON.parse(
                    this.encryptionService.decrypt(
                      error.error,
                      this.sessionKeyString as string
                    )
                  )
                : error.error;

              if (parsedData instanceof ErrorEvent) {
                errorMsg = `Error: ${parsedData.message}`;
              } else {
                if (error.status === 0) {
                  errorMsg = !window.navigator.onLine
                    ? 'Internet not available'
                    : 'Server error';
                } else {
                  if (parsedData?.ErrorMsg) {
                    errorMsg = parsedData.ErrorMsg;
                  } else if (
                    parsedData?.errors &&
                    typeof parsedData.errors === 'object'
                  ) {
                    const errorKeys = Object.keys(parsedData.errors);
                    if (errorKeys.length > 0) {
                      errorMsg = parsedData.errors[errorKeys[0]].toString();
                    } else {
                      errorMsg = 'Unknown error occurred';
                    }
                  } else {
                    errorMsg = 'Error processing request';
                  }
                }
              }

              if (error.statusText === 'Unknown Error' && error.status !== 0) {
                errorMsg = 'Server is unreachable';
              }
            } catch (decryptError) {
              errorMsg =
                error.message || `HTTP ${error.status}: ${error.statusText}`;
            }
          } else {
            errorMsg =
              error.message || `HTTP ${error.status}: ${error.statusText}`;
          }
        } catch (generalError) {
          errorMsg = 'An unexpected error occurred';
        }

        return throwError(() => new Error(errorMsg));
      })
    );
  }
}
